import type {
    InboxSDK as InboxSDKType,
    MessageView,
    ThreadView,
    ToolbarButtonOnClickEvent,
} from '@inboxsdk/core'
import { load } from '@inboxsdk/core'
import './anywhere'
import './types'

export const BASE_LOCATION_PATTERN = /#([^/]+)/
export const LABEL_CATEGORY_PATTERN = /#((?:label|category)\/[^/]+)/
export const SEARCH_PATTERN = /#search\//

const SECONDARY_ACCOUNT_PATTERN = /\/u\/[1-9]\d*\//

const THREAD_ID_PATTERN =
    /#(?:inbox|sent|trash|snoozed|starred|drafts|imp|all|spam|scheduled)\/([^/&?]+)/i
const NESTED_THREAD_ID_PATTERN = /#(?:label|category|search)\/[^/]+\/([^/&?]+)/i

/**
 * Extracts the thread ID directly from a Gmail URL hash.
 * This is more reliable than InboxSDK's getThreadIDAsync() which can return
 * legacy hex IDs that don't work in modern Gmail URLs using base36 IDs.
 */
export function extractThreadIdFromHash(hash: string): string | undefined {
    const cleaned = hash.replace(/[?&]compose=[^&/]*/g, '')

    const nestedMatch = cleaned.match(NESTED_THREAD_ID_PATTERN)
    if (nestedMatch?.[1]) {
        return nestedMatch[1]
    }

    const match = cleaned.match(THREAD_ID_PATTERN)
    if (match?.[1]) {
        return match[1]
    }

    return undefined
}

export function buildEmailHref(threadId: string, loc: Location, userEmail?: string): string {
    const hash = loc.hash.replace(/[?&]compose=[^&/]*/g, '')
    let baseLocation = 'all'

    const hashParts = hash.match(BASE_LOCATION_PATTERN)
    if (hashParts?.[1]) {
        baseLocation = hashParts[1]
    }

    if (baseLocation === 'search' || baseLocation === 'inbox') {
        baseLocation = 'all'
    }

    const labelMatch = hash.match(LABEL_CATEGORY_PATTERN)
    const hashPath = labelMatch?.[1] ?? baseLocation

    const url = new URL(loc.pathname, loc.origin)
    if (userEmail && SECONDARY_ACCOUNT_PATTERN.test(loc.pathname)) {
        url.searchParams.set('authuser', userEmail)
    }
    url.hash = `${hashPath}/${threadId}`
    return url.toString()
}

;(() => {
    function checkGmailHolder(): void {
        if (top?.location.pathname.indexOf('/mail/') === -1 || window !== top) return

        // Check left pos of gmail holders (bottom right)
        const elms = document.querySelectorAll('div.no') as NodeListOf<HTMLElement>
        for (let i = 0; i < elms.length; i++) {
            const elm = elms[i]
            const css_text = elm?.style.cssText || ''
            if (elm && (css_text.indexOf('float: right') !== -1 || elm.style.float === 'right')) {
                let left: number | null = null
                if (elm.offsetWidth > 50) {
                    left = elm.offsetLeft
                }
                if (top.TDOpts.gmail_holder_left_pos !== left) {
                    top.TDOpts.gmail_holder_left_pos = left
                    top.TDOpts.placeWindow()
                }
            }
        }
    }

    setInterval(checkGmailHolder, 300)

    load(2, 'sdk_todoistGmail_bb5f2257e5').then((sdk: InboxSDKType) => {
        const userEmail = sdk.User.getEmailAddress()

        sdk.Toolbars.registerThreadButton({
            title: 'Add to Todoist',
            positions: ['THREAD', 'ROW'],
            iconUrl: 'https://d3ptyyxy2at9ui.cloudfront.net/gmail-plugin-todoist-icon-v4.svg',
            onClick: (event: ToolbarButtonOnClickEvent) => {
                const loc = top?.location
                const view = event.selectedThreadRowViews[0] ?? event.selectedThreadViews[0]

                if (!loc || !view) {
                    top.TDOpts.addAsTask()
                    return
                }

                const title = view.getSubject()
                const urlThreadId = extractThreadIdFromHash(loc.hash)

                if (urlThreadId) {
                    top.TDOpts.addAsTask({
                        title,
                        href: buildEmailHref(urlThreadId, loc, userEmail),
                    })
                    return
                }

                view.getThreadIDAsync()
                    .then((threadId) => {
                        const href = threadId ? buildEmailHref(threadId, loc, userEmail) : loc.href

                        top.TDOpts.addAsTask({ title, href })
                    })
                    .catch(() => {
                        top.TDOpts.addAsTask({ title, href: loc.href })
                    })
            },
        })

        function updateThreadInfo(thread_view: ThreadView): void {
            try {
                const loc = top?.location

                if (!loc) return

                top.TDOpts.email_title = thread_view.getSubject()

                function fnHandleThreadId(thread_id: string | null): void {
                    try {
                        top.TDOpts.email_href = thread_id
                            ? buildEmailHref(thread_id, loc, userEmail)
                            : loc.href

                        if (top.TDOpts.notifyIframeOfEmailChange) {
                            top.TDOpts.notifyIframeOfEmailChange()
                        }
                    } catch {
                        top.TDOpts.email_href = loc.href
                    }
                }

                // Try to infer the thread_id from URL first (works when thread is fully opened)
                const thread_id = extractThreadIdFromHash(loc.hash)

                if (thread_id) {
                    fnHandleThreadId(thread_id)
                } else {
                    // Use InboxSDK to get thread ID (important for reading pane where URL doesn't change)
                    thread_view
                        .getThreadIDAsync()
                        .then(fnHandleThreadId)
                        .catch(() => {
                            fnHandleThreadId(null)
                        })
                }
            } catch {
                // Silent error handling
            }
        }

        // Register handler for when threads are viewed (includes reading pane)
        sdk.Conversations.registerThreadViewHandler((thread_view: ThreadView) => {
            updateThreadInfo(thread_view)
        })

        // Keep the message view handler for redundancy
        sdk.Conversations.registerMessageViewHandler((message_view: MessageView) => {
            const thread_view = message_view.getThreadView()
            updateThreadInfo(thread_view)
        })
    })
})()
